import React, { createContext, useContext, useMemo, useReducer } from 'react';
import PropTypes from 'prop-types';

// Action types
const CATEGORY_ACTIONS = {
    SET_EDITING_CATEGORY_ID: 'SET_EDITING_CATEGORY_ID',
    SET_NEW_CATEGORY: 'SET_NEW_CATEGORY',
    SET_EDITING_SUBCATEGORY_ID: 'SET_EDITING_SUBCATEGORY_ID',
    SET_NEW_EDITING_SUBCATEGORY_ID: 'SET_NEW_EDITING_SUBCATEGORY_ID',
    SET_NEW_SUBCATEGORY_FOR_CATEGORY: 'SET_NEW_SUBCATEGORY_FOR_CATEGORY',
    RESET_STATE: 'RESET_STATE',
};

// Initial state
const initialState = {
    editingCategoryId: null,
    newCategory: null,
    editingSubcategoryId: null,
    newEditingSubcategoryId: null,
    newSubcategoryForCategory: null, // categoryId for which we're adding a new subcategory
};

// Reducer
const categoryReducer = (state, action) => {
    switch (action.type) {
        case CATEGORY_ACTIONS.SET_EDITING_CATEGORY_ID:
            return {
                ...state,
                editingCategoryId: action.payload,
            };
        case CATEGORY_ACTIONS.SET_NEW_CATEGORY:
            return {
                ...state,
                newCategory: action.payload,
            };
        case CATEGORY_ACTIONS.SET_EDITING_SUBCATEGORY_ID:
            return {
                ...state,
                editingSubcategoryId: action.payload,
            };
        case CATEGORY_ACTIONS.SET_NEW_EDITING_SUBCATEGORY_ID:
            return {
                ...state,
                newEditingSubcategoryId: action.payload,
            };
        case CATEGORY_ACTIONS.SET_NEW_SUBCATEGORY_FOR_CATEGORY:
            return {
                ...state,
                newSubcategoryForCategory: action.payload,
            };
        case CATEGORY_ACTIONS.RESET_STATE:
            return initialState;
        default:
            return state;
    }
};

// Create context
const CategoryContext = createContext();

// Context provider component
export const CategoryProvider = ({ children }) => {
    const [state, dispatch] = useReducer(categoryReducer, initialState);

    // Action creators
    const setEditingCategoryId = id => {
        dispatch({
            type: CATEGORY_ACTIONS.SET_EDITING_CATEGORY_ID,
            payload: id,
        });
    };

    const setNewCategory = category => {
        dispatch({
            type: CATEGORY_ACTIONS.SET_NEW_CATEGORY,
            payload: category,
        });
    };

    const setEditingSubcategoryId = id => {
        dispatch({
            type: CATEGORY_ACTIONS.SET_EDITING_SUBCATEGORY_ID,
            payload: id,
        });
    };

    const setNewEditingSubcategoryId = id => {
        dispatch({
            type: CATEGORY_ACTIONS.SET_NEW_EDITING_SUBCATEGORY_ID,
            payload: id,
        });
    };

    const setNewSubcategoryForCategory = categoryId => {
        dispatch({
            type: CATEGORY_ACTIONS.SET_NEW_SUBCATEGORY_FOR_CATEGORY,
            payload: categoryId,
        });
    };

    const resetState = () => {
        dispatch({
            type: CATEGORY_ACTIONS.RESET_STATE,
        });
    };

    const value = useMemo(
        () => ({
            // State
            editingCategoryId: state.editingCategoryId,
            newCategory: state.newCategory,
            editingSubcategoryId: state.editingSubcategoryId,
            newEditingSubcategoryId: state.newEditingSubcategoryId,
            newSubcategoryForCategory: state.newSubcategoryForCategory,
            // Actions
            setEditingCategoryId,
            setNewCategory,
            setEditingSubcategoryId,
            setNewEditingSubcategoryId,
            setNewSubcategoryForCategory,
            resetState,
        }),
        [state]
    );

    return <CategoryContext.Provider value={value}>{children}</CategoryContext.Provider>;
};

CategoryProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

// Custom hook to use the category context
export const useCategoryContext = () => {
    const context = useContext(CategoryContext);
    if (!context) {
        throw new Error('useCategoryContext must be used within a CategoryProvider');
    }
    return context;
};

export default CategoryContext;
